import { computed, Ref, ref, nextTick, watch } from '@vue/composition-api';
import { cloneDeep, uniqBy } from 'lodash-es';
import {
  InterestBehaviorScene,
  AdgroupFormData,
  CampaignFormData,
  INTEREST_LABEL_ID,
  ADVERTISER_FIRST_INDUSTRY_ID,
  SelectTreeLabel,
  InterestBehaviorData,
} from '@tt4b/ttam-enums';
import { useStdAdgroupIs, StateEnum, useStore, useStdObjectiveType, useValidateU18 } from '@tt4b/ttam-hooks';
import { trackEvent, checkGameBadgeValidity, getTagId, getTagScene } from './../../../../utils/index';
import { useInterestBehaviorStore } from './use-interest-behavior-store';
import { useTargetTypePropsVisible } from '../../interests/hooks/target-type';
import { handleInterestLabelTopById, logger } from '@tt4b/ttam-utils';
import { useInterestBehaviorData } from './use-interest-behavior-data';
import { useFuzzySearch } from './use-fuzzy-search';
import store from './store';
const { state: state } = store;
const currentTab = ref(0);
const scrollbarWrap = ref();
const { INTEREST, CREATOR, VIDEO, HASHTAG, KEYWORD, INMARKET } = InterestBehaviorScene;
// 选中后滚动到对应位置
function scrollToCurrentScenePosition(curNode) {
  nextTick(() => {
    let { scene } = curNode;
    if (scene === KEYWORD) {
      scene = INTEREST;
    }
    const ele = document.querySelector(`#tab-item-${scene}`) as HTMLElement | null;
    if (ele) {
      const y = ele.offsetTop || 0;
      scrollbarWrap.value.scrollTo(0, y);
    }
  });
}

export function selectOneSimilarRecommend(curNode) {
  scrollToCurrentScenePosition(curNode);
}
export function useInterestBehavior(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  emit: any;
}) {
  const { campaignData } = props;
  const { adgroupData } = props;
  const { emit } = props;
  const {
    search,
    isSearching,
    searchKeyword,
    searchList,
    onSearchKeywordChange,
    onHideSearchResultList,
    onExposeResultListItem,
    afterClickResultListItem,
    onSelectTreeInteraction,
    subscribeFuzzySearch,
  } = useFuzzySearch({
    adgroupData,
    campaignData,
  });
  const { isLimitU18Interest } = useValidateU18({
    campaignFormData: campaignData,
    adgroupFormData: adgroupData,
  });
  const { isHasTikTok, isOnlyPangle, isSelectedIndustryType, isProductSales } = useStdAdgroupIs({
    campaignFormData: campaignData,
    adgroupFormData: adgroupData,
  });
  const { tagsList, selectedTags, onTagClose } = useInterestBehaviorData({ adgroupData, emit });
  // 是否支持behavior
  const isShowBehavior = computed(() => isHasTikTok.value);
  // hashtag 定向是否可见
  const isHashtagVisible = computed(() => !isSelectedIndustryType.value);
  const { visible: isInMarketTargetAvailable } = useTargetTypePropsVisible({
    campaignData,
    adgroupData,
  });
  const { state: data } = useStore();
  const accountInfo = computed(() => data[StateEnum.accountInfo].data.value);
  const { isAppInstall } = useStdObjectiveType({
    campaignFormData: campaignData,
  });
  const handleTagDisabled = item => {
    if (!isLimitU18Interest.value) {
      return false;
    }
    return [HASHTAG, KEYWORD].includes(item.scene);
  };
  const isGameAdvertiser = accountInfo?.value?.extra?.first_industry_id === ADVERTISER_FIRST_INDUSTRY_ID.GAME;
  // product sales 不支持game label置顶
  const isGameLabelTop = computed(() => !isProductSales.value && isGameAdvertiser && isAppInstall.value);
  const { interestBehaviorListMap, updatePanelData } = useInterestBehaviorStore();
  /**
   * 获取当前tab下已经选择的 tag 节点，非搜索接口返回的不展示（visible: false）
   * 原因：搜索时当前tab下需包含搜索结果列表 + 已经选择的 tag 节点列表（隐藏）
   * @returns
   */
  function getCurrentTabSelectedNodes() {
    const _nodes: InterestBehaviorData[] = [];
    const currentTabScene = state.interestBehaviorList.value[currentTab.value]?.scene;
    logger.info('getCurrentTabSelectedNodes', currentTab.value);
    const selectedTagsId = selectedTags.value;
    selectedTagsId.forEach(id => {
      const scene = getTagScene(id);
      if (scene === currentTabScene) {
        const node = interestBehaviorListMap.value[scene][getTagId(id)];
        // 将children置为空是因为选中的节点中，不关心它的子节点
        if (node) {
          _nodes.push({ ...cloneDeep(node), visible: false, filterable: false, children: [] });
        }
      }

      // interest keywords在interest tab下
      if (currentTabScene === INTEREST && scene === KEYWORD) {
        const node = interestBehaviorListMap.value[INTEREST][getTagId(id)];
        if (node) {
          _nodes.push({ ...cloneDeep(node), visible: false, filterable: false, children: [] });
        }
      }
    });

    return _nodes;
  }

  const currentTabSelectedNodes = computed(() => getCurrentTabSelectedNodes());
  // 游戏广告主，game标签置顶
  function reSortInterestForGameAdvertiser(panelData) {
    if (isGameLabelTop.value) {
      const interestListInPanelData = panelData?.find(item => item.scene === INTEREST) || {
        data: [],
      };

      const interestList = interestListInPanelData.data;
      interestListInPanelData.data = handleInterestLabelTopById(interestList, INTEREST_LABEL_ID.GAME, 'tag_id');
    }
  }
  // 搜索框的数据
  const panelData = computed(() => {
    let _panelData = cloneDeep(state.interestBehaviorList.value);
    if (!_panelData?.length) {
      return [];
    }
    if (searchKeyword.value) {
      if (searchList?.value?.length) {
        const list = uniqBy([...cloneDeep(searchList.value), ...currentTabSelectedNodes.value], 'id');
        list.forEach(v => {
          if (handleTagDisabled(v)) {
            v.disabled = true;
          }
        });
        _panelData[currentTab.value].data = list;
      } else {
        _panelData[currentTab.value].data = [];
      }

      return _panelData;
    }
    if (isLimitU18Interest.value) {
      _panelData.forEach(v => {
        if (v.scene === HASHTAG) {
          v.disabled = true;
        }
      });
    }
    reSortInterestForGameAdvertiser(_panelData);

    // - 仅在包含Tiktok版位时支持behavior
    if (!isShowBehavior.value) {
      _panelData = _panelData.filter(item => ![VIDEO, CREATOR, HASHTAG].includes(item.scene));
    }
    // - HEC不支持hashtag
    if (!isHashtagVisible.value) {
      _panelData = _panelData.filter(item => ![HASHTAG].includes(item.scene));
    }
    // - 直投pangle：不支持兴趣和行为定向
    if (!isInMarketTargetAvailable.value) {
      _panelData = _panelData.filter(item => ![INMARKET].includes(item.scene));
    }
    return _panelData;
  });

  function checkIsHideBadge(node) {
    if (!isGameLabelTop.value) {
      return true;
    }
    const isGameBadgeValidity = checkGameBadgeValidity();
    if (isGameBadgeValidity) {
      return !(node.tag_id === INTEREST_LABEL_ID.GAME && isGameLabelTop.value);
    } else {
      return true;
    }
  }

  // 切换tab
  function onTabChange(index) {
    currentTab.value = index;
    const adgroupId = adgroupData.value?.adId || '';
    trackEvent('creat_campaign_targeting_searchbox_taxonomy_tab_click', {
      adgroup_id: adgroupId,
      tab_type: panelData.value[index]?.scene,
    });
  }
  const treeTips = computed(() => {
    const scene = panelData.value[currentTab.value]?.scene;
    return SelectTreeLabel()[scene];
  });
  const isShowTreeTips = computed(() => treeTips.value && !searchKeyword.value);
  // 选择tag
  const onSelectedChange = function (keys, nodes, curNode, isChecked) {
    if (searchKeyword.value) {
      updatePanelData(nodes);
    }
    if (isChecked) {
      scrollToCurrentScenePosition(curNode);
    }
  };
  const selectTreeRef = ref();
  const limitU18InterestSelectedTags = computed(() => {
    if (!isLimitU18Interest.value) {
      return [];
    }
    // 选择了
    let disabledTags = [];
    tagsList.value.forEach(tag => {
      if (tag.selected.length) {
        tag.selected.forEach(item => {
          if (handleTagDisabled(item)) {
            disabledTags = disabledTags.concat(item);
          }
        });
      }
    });
    return disabledTags;
  });
  const removeU18LimitTags = () => {
    limitU18InterestSelectedTags.value.forEach(v => {
      onTagClose(v);
    });
  };
  watch(isLimitU18Interest, val => {
    if (val) {
      const scene = panelData.value[currentTab.value]?.scene;

      if (scene === HASHTAG) {
        selectTreeRef?.value?.onTabClick?.(0);
      }
      nextTick(() => {
        removeU18LimitTags?.();
      });
    }
  });
  return {
    panelData,
    treeTips,
    isShowTreeTips,
    isGameLabelTop,
    checkIsHideBadge,
    onTabChange,
    onSelectedChange,
    scrollbarWrap,
    tagsList,
    selectedTags,
    onTagClose,
    search,
    isSearching,
    searchKeyword,
    searchList,
    onSearchKeywordChange,
    onHideSearchResultList,
    onExposeResultListItem,
    afterClickResultListItem,
    onSelectTreeInteraction,
    subscribeFuzzySearch,
    isLimitU18Interest,
    handleTagDisabled,
    selectTreeRef,
    limitU18InterestSelectedTags,
    removeU18LimitTags,
  };
}
